import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Heading } from '@spark-web/heading';
import { Inline } from '@spark-web/inline';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { FC, useRef } from 'react';

import { Button as BrighteButton } from '~/components/common/button';
import { usePartner } from '~/hooks/usePartner';
import { usePartnerTheming } from '~/hooks/usePartnerTheming';
import { useLeadContext } from '~/provider/LeadProvider';
import { useVendorContext } from '~/provider/VendorProvider';

import { ArrowRightIcon } from '../common/icon';
import { SelectedVendor } from '../vendors/vendors-list/selected-vendor';

import { VendorSelection } from './vendor-selection';

type ReviewSelectionProps = {
  isMobile?: boolean;
};

export const ReviewSelection: FC<ReviewSelectionProps> = ({ isMobile = false }) => {
  const { lead } = useLeadContext();
  const partner = usePartner();
  const {
    card: { borderRadius, borderLeft, css: cardCss },
  } = usePartnerTheming();

  const { hasVendorsSelected, selectedVendors, previouslySelectedVendorIds, setRequestQuoteModal, reviewQuoteRef } =
    useVendorContext();

  const title = hasVendorsSelected ? `Here's your quote requests` : 'Select vendors to continue';
  const contact = lead?.contact;

  const mobileReviewRef = useRef<HTMLButtonElement>(null);
  const reviewRef = useRef<HTMLButtonElement>(null);

  const openRequestQuoteModal = (modalRef: React.RefObject<HTMLButtonElement | null>) => {
    if (modalRef.current) reviewQuoteRef.current = modalRef.current;
    setRequestQuoteModal(true);
  };

  if (isMobile) {
    const vendorCount = previouslySelectedVendorIds.length + selectedVendors.length;

    return (
      <>
        {hasVendorsSelected && (
          <Box
            display="flex"
            flexDirection="column"
            background="surface"
            padding="xlarge"
            gap="large"
            justifyContent="center"
            shadow="large"
            data={{ testId: 'review-selection' }}
          >
            <BrighteButton
              ref={mobileReviewRef}
              onClick={() => openRequestQuoteModal(mobileReviewRef)}
              rounded
              label=""
            >
              <Inline gap="xsmall">
                Check and confirm
                <ArrowRightIcon partner={partner} />
              </Inline>
            </BrighteButton>
            <Text align="center" size="small">
              {vendorCount} vendor{vendorCount > 1 ? 's' : ''} added
            </Text>
          </Box>
        )}
        <VendorSelection isMobile={isMobile} />
      </>
    );
  }

  return (
    <Box
      marginLeft="xsmall"
      borderLeft={borderLeft}
      borderRadius={borderRadius}
      background="surface"
      css={css({ ...cardCss, maxWidth: 360, borderLeftWidth: 4, top: 10, minWidth: '350px' })}
      position="sticky"
    >
      <Stack gap="large" paddingX="large" paddingY="xlarge">
        <Heading level="2">{contact?.firstName ? `${contact?.firstName}, ${title.toLowerCase()}` : title}</Heading>
        {hasVendorsSelected && (
          <BrighteButton ref={reviewRef} onClick={() => openRequestQuoteModal(reviewRef)} rounded label="">
            <Inline gap="xsmall">
              Check and confirm
              <ArrowRightIcon partner={partner} />
            </Inline>
          </BrighteButton>
        )}
        <VendorSelection isMobile={isMobile} />
      </Stack>
      {selectedVendors.length > 0 && (
        <SelectedVendor
          onRequestQuotes={() => {
            openRequestQuoteModal(isMobile ? mobileReviewRef : reviewRef);
          }}
        />
      )}
    </Box>
  );
};
